import React, { useEffect, useState } from "react";
import { IconType } from "react-icons";
import { TbAddressBook, TbBuildingStore, TbChevronRight, TbMessages, TbPhoneCall, TbReportAnalytics, TbSettings, TbUsers } from "react-icons/tb";
import { useLocation } from "react-router-dom";
import { CRM_PATHS, isCrmScreenLive } from "../../pages/crm/crmPaths";
import { FollowUpsDueBadge } from "../../pages/crm/FollowUpsDueBadge";
import { MatchReviewWaitingBadge } from "../../pages/crm/MatchReviewWaitingBadge";
import {
  ActiveSubmenuLink,
  Branch,
  SectionToggle,
  SidebarBackdrop,
  SidebarContainer,
  SubmenuLink,
} from "./sidebar.elements";
import { SidebarReports } from "./SidebarReports";

interface ItemInterface {
  link: string;
  label: string;
  /** Rendered at the end of the row, e.g. a count. */
  badge?: React.ComponentType;
}

interface SectionInterface {
  heading: string;
  icon: IconType;
  items: Array<ItemInterface>;
  /** Draws the whole group itself, because its submenu has submenus of its own. */
  panel?: React.ComponentType<{ onClose?: () => void }>;
}

/**
 * The CRM sections, in every build. Each entry needs a row in SCREENS in
 * src/pages/crm/CrmRoutes.tsx — the two lists are independent. In production
 * an entry still in CRM_PENDING (crmPaths.ts) is left out, and a section with
 * no entry left has no heading either.
 */
const crmPeopleItems: Array<ItemInterface> = [
  {
    label: "Contacts",
    link: CRM_PATHS.contacts,
  },
  {
    label: "Lists",
    link: CRM_PATHS.lists,
  },
  {
    label: "Tags",
    link: CRM_PATHS.tags,
  },
  {
    label: "Batch updates",
    link: CRM_PATHS.batchUpdates,
  },
  {
    label: "Match Review",
    link: CRM_PATHS.matchReview,
    badge: MatchReviewWaitingBadge,
  },
  {
    label: "Import",
    link: CRM_PATHS.import,
  },
];

const crmOutreachItems: Array<ItemInterface> = [
  {
    label: "Call Queue",
    link: CRM_PATHS.callQueue,
  },
  {
    label: "Follow-ups",
    link: CRM_PATHS.followUps,
    badge: FollowUpsDueBadge,
  },
  {
    label: "Call Log",
    link: CRM_PATHS.callLog,
  },
  {
    label: "Email",
    link: CRM_PATHS.email,
  },
  {
    label: "Workflows",
    link: CRM_PATHS.workflows,
  },
];

// const SIDE_NAV_WIDTH = 280;

const peopleItems: Array<ItemInterface> = [
  {
    label: "Users",
    link: "/users",
  },
  // {
  //   label: 'Achievements',
  //   link: '/achievements',
  //   icon: FaPeopleCarry,
  // },
  {
    label: "Banned Members",
    link: "/user-bans",
  },
  {
    label: "Feedback",
    link: "/feedback",
  },
  // {
  //   label: "Leaderboard",
  //   link: "/leaderboard",
  //   icon: TbUsersGroup,
  // },
];

const placesItems: Array<ItemInterface> = [
  {
    label: "Businesses",
    link: "/businesses",
  },
  {
    label: "Categories",
    link: "/categories",
  },
  {
    label: "Reviews",
    link: "/reviews",
  },
];

const messagesItems: Array<ItemInterface> = [
  {
    label: "Notification",
    link: "/notification-new",
  },
  {
    label: "Send SMS",
    link: "/sms",
  },
  {
    label: "Send WhatsApp",
    link: "/whatsapp",
  },
  {
    label: "WhatsApp Messages",
    link: "/whatsapp-messages",
  },
];

const settingsItems: Array<ItemInterface> = [
  {
    label: "App Config",
    link: "/app-config",
  },
  {
    label: "Rate Limits",
    link: "/rate-limits",
  },
  {
    label: "Country Blocking",
    link: "/country-blocking",
  },
];

const liveOnly = (crmItems: Array<ItemInterface>) => crmItems.filter((item) => isCrmScreenLive(item.link));

const sections: Array<SectionInterface> = [
  { heading: "CRM", icon: TbAddressBook, items: liveOnly(crmPeopleItems) },
  { heading: "Outreach", icon: TbPhoneCall, items: liveOnly(crmOutreachItems) },
  { heading: "Reports", icon: TbReportAnalytics, items: [], panel: SidebarReports },
  { heading: "People", icon: TbUsers, items: peopleItems },
  { heading: "Places", icon: TbBuildingStore, items: placesItems },
  { heading: "Messages", icon: TbMessages, items: messagesItems },
  { heading: "Settings", icon: TbSettings, items: settingsItems },
].filter((section) => section.items.length > 0 || section.panel);

/**
 * An entry is active on its own path and on any path beneath it, so a detail
 * page (/crm/contacts/42, /users/7) keeps its list highlighted. Matching on a
 * segment boundary keeps /notification from claiming /notification-new.
 */
export const isActiveLink = (pathname: string, link: string) =>
  pathname === link || pathname.startsWith(`${link}/`);

/** The section holding the page at `pathname`, if any. */
const sectionHolding = (pathname: string) =>
  sections.find((section) => section.items.some((item) => isActiveLink(pathname, item.link)))?.heading;

/**
 * Which sections are open. All start closed, except the one
 * holding the page the dashboard opened on; arriving at a page in a closed
 * section (a link in a screen, the back button) opens it, so the highlighted
 * entry is never hidden. Nothing is remembered between page loads.
 */
const useOpenSections = (pathname: string) => {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => {
    const holding = sectionHolding(pathname);
    return new Set(holding ? [holding] : []);
  });

  useEffect(() => {
    const holding = sectionHolding(pathname);
    if (holding) setOpen((current) => (current.has(holding) ? current : new Set([...Array.from(current), holding])));
  }, [pathname]);

  const toggle = (heading: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(heading)) next.add(heading);
      return next;
    });

  return { open, toggle };
};

interface SidebarProps {
  width: string | number;
  /** Mobile mode: float over the content instead of sitting beside it. */
  overlay?: boolean;
  /** Called when the sidebar should close (link click / backdrop click on mobile). */
  onClose?: () => void;
}

export const Sidebar = ({ width, overlay = false, onClose }: SidebarProps) => {
  const { pathname } = useLocation();
  const { open, toggle } = useOpenSections(pathname);

  const showLinks = (items: Array<ItemInterface>) => (
    <Branch>
      {items.map((item) => {
        const active = isActiveLink(pathname, item.link);
        const Link = active ? ActiveSubmenuLink : SubmenuLink;
        const Badge = item.badge;

        return (
          <Link key={item.link} to={item.link} onClick={onClose} aria-current={active ? "page" : undefined}>
            {item.label}
            {Badge && <Badge />}
          </Link>
        );
      })}
    </Branch>
  );

  const showSections = () =>
    sections.map((section) => {
      if (section.panel) return <section.panel key={section.heading} onClose={onClose} />;

      const expanded = open.has(section.heading);
      const listId = `sidebar-section-${section.heading.toLowerCase()}`;
      const Icon = section.icon;
      // A closed section still shows its entries' counts, on its heading.
      const badges = expanded ? [] : section.items.filter((item) => item.badge);

      return (
        <nav key={section.heading} aria-label={section.heading}>
          <SectionToggle
            type="button"
            aria-expanded={expanded}
            aria-controls={listId}
            data-current={section.items.some((item) => isActiveLink(pathname, item.link))}
            onClick={() => toggle(section.heading)}
          >
            <Icon size={18} aria-hidden="true" />
            <span>{section.heading}</span>
            {badges.map(({ link, badge: Badge }) => Badge && <Badge key={link} />)}
            <TbChevronRight size={14} aria-hidden="true" />
          </SectionToggle>
          <div id={listId}>{expanded && showLinks(section.items)}</div>
        </nav>
      );
    });

  return (
    <>
      {overlay && Boolean(width) && <SidebarBackdrop onClick={onClose} />}

      <SidebarContainer width={width} overlay={overlay}>
        <div>
          {/* <LogoContainer>
                <i className="bx bx-layer nav_logo-icon">Admin App</i>
              </LogoContainer> */}

          {showSections()}
        </div>
      </SidebarContainer>
    </>
  );
};
